import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type {
  Conversation,
  Message,
  ServerFrame,
  WsConnectionState,
} from '@talkvibe/shared';

import { chatService } from '../services/chat.ts';
import { RealtimeConnection } from '../services/websocket.ts';
import { useGameStore } from './game.ts';

/**
 * A message the user has sent but the server has not yet confirmed.
 * `status` is what lets the bubble show a spinner, and lets a failed send be
 * retried or rolled back instead of silently vanishing.
 */
export interface OutgoingMessage extends Message {
  clientId: string;
  status: 'sending' | 'failed';
}

export type ChatMessage = Message | OutgoingMessage;

export function isPending(message: ChatMessage): message is OutgoingMessage {
  return 'status' in message;
}

const TYPING_TIMEOUT_MS = 4000;

export const useChatStore = defineStore('chat', () => {
  const conversations = ref<Conversation[]>([]);
  const conversationsCursor = ref<string | null>(null);
  const conversationsLoading = ref(false);

  /**
   * Marketplace threads, held apart from `conversations`.
   *
   * A second list rather than a filter over the first. They are fetched with
   * separate cursors because the server paginates each scope on its own, and
   * they are *shown* in separate places — a buyer asking the price of a
   * bicycle does not belong in the list of people you talk to.
   */
  const listingConversations = ref<Conversation[]>([]);
  const listingConversationsLoading = ref(false);
  const listingConversationsLoaded = ref(false);

  const activeConversation = ref<Conversation | null>(null);
  /** Oldest first - the order the transcript is rendered in. */
  const messages = ref<ChatMessage[]>([]);
  const messagesCursor = ref<string | null>(null);
  const messagesLoading = ref(false);
  const hasMoreHistory = computed(() => messagesCursor.value !== null);

  const typingUserIds = ref<string[]>([]);
  const onlineUserIds = ref<string[]>([]);
  const connectionStatus = ref<WsConnectionState>('disconnected');
  /** messageId the other side has read up to. */
  const readMarkers = ref<Record<string, string>>({});

  let connection: RealtimeConnection | null = null;
  const typingTimers = new Map<string, number>();

  /**
   * The badge, from whichever source actually knows.
   *
   * Once the conversation list is loaded it is the truth — it is the thing the
   * user is looking at, and reading a chat updates it immediately. Before
   * that, the only number available is the one the server sends with each
   * activity event, which is what keeps the badge right for someone who has
   * been on the feed since they signed in.
   */
  const listUnread = computed(() =>
    conversations.value.reduce((sum, conversation) => sum + conversation.unreadCount, 0),
  );
  const conversationsLoaded = ref(false);
  const serverUnread = ref<number | null>(null);
  const totalUnread = computed(() =>
    conversationsLoaded.value ? listUnread.value : (serverUnread.value ?? 0),
  );

  /* ---------------------------------------------------------------------- */
  /* Conversation list                                                       */
  /* ---------------------------------------------------------------------- */

  async function loadConversations(): Promise<void> {
    conversationsLoading.value = true;
    try {
      // `chats` only: marketplace threads have their own list below. Without
      // the scope this list would carry both, which is the thing the separate
      // type exists to prevent.
      const page = await chatService.conversations(undefined, 'chats');
      conversations.value = page.items;
      conversationsLoaded.value = true;
      conversationsCursor.value = page.nextCursor;
    } finally {
      conversationsLoading.value = false;
    }
  }

  /**
   * The marketplace inbox.
   *
   * Filtered on `type` as well as asked for by scope: a server that predates
   * marketplace conversations ignores an unknown query parameter and answers
   * with every conversation, and this list showing somebody their whole inbox
   * under a Marketplace heading is worse than it being empty.
   */
  async function loadListingConversations(): Promise<void> {
    listingConversationsLoading.value = true;
    try {
      const page = await chatService.conversations(undefined, 'listings');
      listingConversations.value = page.items.filter((row) => row.type === 'listing');
      listingConversationsLoaded.value = true;
    } finally {
      listingConversationsLoading.value = false;
    }
  }

  async function loadMoreConversations(): Promise<void> {
    if (!conversationsCursor.value || conversationsLoading.value) return;
    conversationsLoading.value = true;
    try {
      const page = await chatService.conversations(conversationsCursor.value, 'chats');
      conversations.value = [...conversations.value, ...page.items];
      conversationsCursor.value = page.nextCursor;
    } finally {
      conversationsLoading.value = false;
    }
  }

  async function startConversation(userId: string): Promise<Conversation> {
    const conversation = await chatService.createConversation(userId);
    if (!conversations.value.some((entry) => entry.id === conversation.id)) {
      conversations.value = [conversation, ...conversations.value];
    }
    return conversation;
  }

  /* ---------------------------------------------------------------------- */
  /* Opening a conversation                                                  */
  /* ---------------------------------------------------------------------- */

  /**
   * Loads a conversation and connects its socket. Safe to call repeatedly for
   * the same id - the second call is a no-op rather than a second socket.
   */
  async function openConversation(conversationId: string): Promise<void> {
    if (activeConversation.value?.id === conversationId && connection) return;

    closeConversation();
    messagesLoading.value = true;
    try {
      const [conversation, page] = await Promise.all([
        chatService.conversation(conversationId),
        chatService.messages(conversationId),
      ]);
      activeConversation.value = conversation;
      // The API returns newest first; the transcript reads oldest first.
      messages.value = [...page.items].reverse();
      messagesCursor.value = page.nextCursor;
      seedReadMarkers(conversation);
      connect(conversationId);
      await markLatestRead();
    } finally {
      messagesLoading.value = false;
    }
  }

  /** Fetches the previous page when the user scrolls to the top. */
  async function loadOlderMessages(): Promise<void> {
    const conversation = activeConversation.value;
    if (!conversation || !messagesCursor.value || messagesLoading.value) return;

    messagesLoading.value = true;
    try {
      const page = await chatService.messages(conversation.id, messagesCursor.value);
      messages.value = [...[...page.items].reverse(), ...messages.value];
      messagesCursor.value = page.nextCursor;
    } finally {
      messagesLoading.value = false;
    }
  }

  function closeConversation(): void {
    connection?.disconnect();
    connection = null;
    activeConversation.value = null;
    messages.value = [];
    messagesCursor.value = null;
    typingUserIds.value = [];
    onlineUserIds.value = [];
    readMarkers.value = {};
    connectionStatus.value = 'disconnected';
    for (const timer of typingTimers.values()) window.clearTimeout(timer);
    typingTimers.clear();
  }

  /* ---------------------------------------------------------------------- */
  /* Sending                                                                 */
  /* ---------------------------------------------------------------------- */

  /**
   * Shows the message immediately, then sends it.
   *
   * The optimistic bubble carries a `clientId`; the server echoes it back on
   * the persisted message, which is how the two are matched up. If the send
   * fails the bubble is marked failed rather than removed, so the text the
   * user typed is never lost.
   */
  /**
   * Sends a message: words, a photo, or both.
   *
   * `photo` is already uploaded by the time it gets here — the composer does that
   * when the file is chosen — so this carries an asset id and a local preview URL.
   * The preview is what the optimistic bubble shows: the real asset is still being
   * resized, and a bubble that waits for the server's URL would show a grey box
   * for a second on a picture the sender is looking at right now.
   */
  async function sendMessage(
    content: string,
    senderId: string,
    senderLanguage: string,
    /**
     * The attachment, and what it is.
     *
     * `kind` is why this is not still called `photo`: a recording and a picture
     * both arrive as a media id with something to show while the server catches
     * up, and the bubble has to know which it is drawing from the first frame.
     * `preview` is an object URL in both cases — the local file, so nothing
     * waits on a round trip.
     */
    attachment?: { mediaId: string; preview: string; kind?: 'message' | 'voice' },
    replyTo?: Message,
  ): Promise<void> {
    const conversation = activeConversation.value;
    if (!conversation) return;

    const clientId = `tmp_${crypto.randomUUID()}`;
    const optimistic: OutgoingMessage = {
      id: clientId,
      clientId,
      conversationId: conversation.id,
      senderId,
      type: attachment ? (attachment.kind === 'voice' ? 'voice' : 'image') : 'text',
      // Null rather than '': the server stores no caption the same way, so the
      // optimistic bubble and the one that replaces it render identically.
      content: content.length > 0 ? content : null,
      media: attachment
        ? {
            id: attachment.mediaId,
            kind: attachment.kind ?? 'message',
            url: attachment.preview,
            status: 'pending',
            variants: [],
            width: null,
            height: null,
            // Unknown until the server has measured it, which is also what the
            // player reads as "not ready": the sender's own bubble shows the
            // same waiting state everyone else's does.
            durationMs: null,
          }
        : null,
      // Quoted from what is already on screen, so the bubble shows the right line
      // before the server has answered.
      replyTo: replyTo
        ? {
            id: replyTo.id,
            senderId: replyTo.senderId,
            content: replyTo.content,
            hasMedia: replyTo.media !== null,
            // The quoted photo is already on screen in the message being replied
            // to, so the thumbnail is there from the first frame rather than
            // appearing when the server answers.
            media: replyTo.media,
            deleted: replyTo.deletedAt !== null,
          }
        : null,
      replyToId: replyTo?.id ?? null,
      likeCount: 0,
      liked: false,
      sourceLanguage: senderLanguage,
      translation: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      deletedAt: null,
      status: 'sending',
    };
    messages.value = [...messages.value, optimistic];

    // Prefer the socket: it is already open and gives the server a clientId to
    // echo. Fall back to HTTP when the connection is down.
    if (connection && connectionStatus.value === 'connected') {
      connection.send({
        type: 'message',
        conversationId: conversation.id,
        payload: {
          content,
          clientId,
          ...(attachment ? { mediaId: attachment.mediaId } : {}),
          ...(replyTo ? { replyToId: replyTo.id } : {}),
        },
      });
      return;
    }

    try {
      const saved = await chatService.sendMessage(
        conversation.id,
        content,
        attachment?.mediaId,
        replyTo?.id,
      );
      replaceOptimistic(clientId, saved);
    } catch {
      markFailed(clientId);
    }
  }

  /** Re-sends a failed message, reusing its bubble. */
  async function retryMessage(clientId: string): Promise<void> {
    const conversation = activeConversation.value;
    const pending = messages.value.find(
      (message) => isPending(message) && message.clientId === clientId,
    );
    // Words or a photo. Guarding on `content` alone refused to retry a photo
    // sent with no caption, which is the one that most needs retrying.
    if (!conversation || !pending || !(pending.content || pending.media)) return;

    messages.value = messages.value.map((message) =>
      isPending(message) && message.clientId === clientId
        ? { ...message, status: 'sending' as const }
        : message,
    );

    const mediaId = pending.media?.id;
    if (connection && connectionStatus.value === 'connected') {
      connection.send({
        type: 'message',
        conversationId: conversation.id,
        payload: {
          content: pending.content ?? '',
          clientId,
          ...(mediaId ? { mediaId } : {}),
        },
      });
      return;
    }

    try {
      const saved = await chatService.sendMessage(
        conversation.id,
        pending.content ?? '',
        mediaId,
      );
      replaceOptimistic(clientId, saved);
    } catch {
      markFailed(clientId);
    }
  }

  /** Removes a failed message the user has given up on. */
  function discardMessage(clientId: string): void {
    messages.value = messages.value.filter(
      (message) => !(isPending(message) && message.clientId === clientId),
    );
  }

  function sendTyping(isTyping: boolean): void {
    const conversation = activeConversation.value;
    if (!conversation || !connection) return;
    connection.send({ type: 'typing', conversationId: conversation.id, payload: { isTyping } });
  }

  async function markLatestRead(): Promise<void> {
    const conversation = activeConversation.value;
    if (!conversation) return;

    const latest = [...messages.value].reverse().find((message) => !isPending(message));
    if (!latest) return;

    if (connection && connectionStatus.value === 'connected') {
      connection.send({
        type: 'read',
        conversationId: conversation.id,
        payload: { messageId: latest.id },
      });
    } else {
      try {
        await chatService.markRead(conversation.id, latest.id);
      } catch {
        // Read state is not worth surfacing an error for.
      }
    }
    clearUnread(conversation.id);
  }

  /* ---------------------------------------------------------------------- */
  /* Realtime                                                                */
  /* ---------------------------------------------------------------------- */

  function connect(conversationId: string): void {
    connection = new RealtimeConnection(conversationId, {
      onFrame: handleFrame,
      onStateChange: (state) => {
        connectionStatus.value = state;
      },
    });
    void connection.connect();
  }

  function handleFrame(frame: ServerFrame): void {
    switch (frame.type) {
      case 'message':
        handleIncomingMessage(frame.payload);
        return;
      case 'message.like':
        applyMessageLike(frame.payload.messageId, frame.payload.likeCount);
        return;
      case 'typing':
        handleTyping(frame.payload.userId, frame.payload.isTyping);
        return;
      case 'read':
        readMarkers.value = { ...readMarkers.value, [frame.payload.userId]: frame.payload.messageId };
        return;
      case 'presence':
        onlineUserIds.value = frame.payload.onlineUserIds;
        return;
      // The game moved. Handed straight to its own store rather than kept
      // here: the chat store is about messages, and a board living in it would
      // be cleared and reloaded by every message-list concern that touches it.
      case 'game':
        useGameStore().receive(frame.payload.game);
        return;
      case 'error':
        // A rejected send must not leave a bubble spinning forever.
        if (frame.payload.clientId) markFailed(frame.payload.clientId);
        return;
      default:
        return;
    }
  }

  /**
   * A message arriving on the socket: new, or an update to one already here.
   *
   * It used to **ignore** anything whose id it already had, to avoid duplicates on a
   * reconnect redelivery. That also ignored the re-announce a deletion sends, so the
   * other person's bubble kept its text until the page was reloaded. Replacing in
   * place fixes both: a redelivery writes the same thing twice, and a deletion lands.
   *
   * `liked` is preserved across the replacement. The frames the room builds are per
   * *language*, not per person, so they carry `liked: null` for everybody — taking it
   * at face value would clear the reader's own like every time somebody sent anything.
   */
  function handleIncomingMessage(payload: Message & { clientId?: string }): void {
    if (payload.clientId) {
      replaceOptimistic(payload.clientId, payload);
      return;
    }

    const existing = messages.value.find((message) => message.id === payload.id);
    if (existing) {
      const merged: Message = {
        ...payload,
        liked: payload.liked ?? existing.liked,
        likeCount: payload.likeCount,
      };
      messages.value = messages.value.map((message) =>
        message.id === payload.id ? merged : message,
      );
      bumpConversation(merged);
      return;
    }

    messages.value = [...messages.value, payload];
    bumpConversation(payload);
    void markLatestRead();
  }

  /**
   * Somebody's like landed.
   *
   * The count only: `liked` belongs to whoever is reading, and the frame deliberately
   * does not carry it.
   */
  function applyMessageLike(messageId: string, likeCount: number): void {
    messages.value = messages.value.map((message) =>
      message.id === messageId ? { ...message, likeCount } : message,
    );
  }

  function handleTyping(userId: string, isTyping: boolean): void {
    const existing = typingTimers.get(userId);
    if (existing !== undefined) window.clearTimeout(existing);

    if (!isTyping) {
      typingTimers.delete(userId);
      typingUserIds.value = typingUserIds.value.filter((id) => id !== userId);
      return;
    }

    if (!typingUserIds.value.includes(userId)) {
      typingUserIds.value = [...typingUserIds.value, userId];
    }
    // Expire it ourselves: a client that disconnects mid-typing never sends
    // the matching `isTyping: false`.
    typingTimers.set(
      userId,
      window.setTimeout(() => {
        typingTimers.delete(userId);
        typingUserIds.value = typingUserIds.value.filter((id) => id !== userId);
      }, TYPING_TIMEOUT_MS),
    );
  }

  /* ---------------------------------------------------------------------- */
  /* Internals                                                               */
  /* ---------------------------------------------------------------------- */

  function replaceOptimistic(clientId: string, saved: Message): void {
    let replaced = false;
    messages.value = messages.value.map((message) => {
      if (isPending(message) && message.clientId === clientId) {
        replaced = true;
        // The optimistic bubble showed the local file through an object URL. The
        // composer hands that URL over rather than revoking it on submit — the
        // bubble is still using it — so this is where it is let go, once the
        // server's own URL has taken its place.
        //
        // A *failed* send keeps its URL on purpose: the bubble stays on screen
        // with a retry button and still has to show the picture. Those are freed
        // when the conversation closes.
        if (message.media?.url.startsWith('blob:')) URL.revokeObjectURL(message.media.url);
        return saved;
      }
      return message;
    });
    if (!replaced && !messages.value.some((message) => message.id === saved.id)) {
      messages.value = [...messages.value, saved];
    }
    bumpConversation(saved);
  }

  /**
   * Removes one of your own messages.
   *
   * The row stays — read markers anchor to it — so the bubble becomes "deleted"
   * rather than disappearing. The server re-announces it, which every other open
   * client now applies.
   */
  async function removeMessage(messageId: string): Promise<void> {
    const deleted = await chatService.deleteMessage(messageId);
    messages.value = messages.value.map((message) =>
      message.id === messageId ? deleted : message,
    );
  }

  /**
   * Likes a message, or unlikes it.
   *
   * Optimistic on both the flag and the count, with the count captured before the
   * change — restoring it from the already-updated value would leave a failed like
   * counted, which is a mistake this codebase has made once already.
   */
  async function toggleMessageLike(messageId: string): Promise<void> {
    const target = messages.value.find((message) => message.id === messageId);
    if (!target) return;
    const was = target.liked === true;
    const wasCount = target.likeCount;

    const apply = (liked: boolean, likeCount: number): void => {
      messages.value = messages.value.map((message) =>
        message.id === messageId ? { ...message, liked, likeCount } : message,
      );
    };

    apply(!was, Math.max(0, wasCount + (was ? -1 : 1)));
    try {
      const result = was
        ? await chatService.unlikeMessage(messageId)
        : await chatService.likeMessage(messageId);
      apply(!was, result.likeCount);
    } catch {
      apply(was, wasCount);
    }
  }

  function markFailed(clientId: string): void {
    messages.value = messages.value.map((message) =>
      isPending(message) && message.clientId === clientId
        ? { ...message, status: 'failed' as const }
        : message,
    );
  }

  /**
   * A message landed in one of this user's conversations, delivered over the
   * user channel rather than the chat socket — so it arrives whether or not
   * the chat is open.
   *
   * The event carries no body on purpose: the preview has to be translated
   * into this reader's language, and doing that per recipient inside a fan-out
   * would put a translation on the send path. Reloading the list is one
   * request and yields a preview that is actually correct.
   */
  function receiveActivity(payload: { conversationId: string; totalUnread: number }): void {
    serverUnread.value = payload.totalUnread;
    if (conversationsLoaded.value) scheduleConversationRefresh();
  }

  let refreshTimer: number | null = null;

  /** Coalesces a burst of messages into one refresh. */
  function scheduleConversationRefresh(): void {
    if (refreshTimer !== null) return;
    refreshTimer = window.setTimeout(() => {
      refreshTimer = null;
      void loadConversations();
    }, 300);
  }

  /** Moves a conversation to the top of the list and refreshes its preview. */
  function bumpConversation(message: Message): void {
    const index = conversations.value.findIndex((entry) => entry.id === message.conversationId);
    if (index === -1) return;
    const conversation = conversations.value[index]!;
    const updated: Conversation = {
      ...conversation,
      lastMessage: message,
      updatedAt: message.createdAt,
    };
    conversations.value = [
      updated,
      ...conversations.value.slice(0, index),
      ...conversations.value.slice(index + 1),
    ];
  }

  function clearUnread(conversationId: string): void {
    conversations.value = conversations.value.map((entry) =>
      entry.id === conversationId ? { ...entry, unreadCount: 0 } : entry,
    );
    if (activeConversation.value?.id === conversationId) {
      activeConversation.value = { ...activeConversation.value, unreadCount: 0 };
    }
  }

  function seedReadMarkers(conversation: Conversation): void {
    const markers: Record<string, string> = {};
    for (const participant of conversation.participants) {
      if (participant.lastReadMessageId) markers[participant.id] = participant.lastReadMessageId;
    }
    readMarkers.value = markers;
  }

  return {
    conversations,
    conversationsCursor,
    conversationsLoading,
    listingConversations,
    listingConversationsLoading,
    listingConversationsLoaded,
    loadListingConversations,
    activeConversation,
    messages,
    messagesLoading,
    hasMoreHistory,
    typingUserIds,
    onlineUserIds,
    connectionStatus,
    readMarkers,
    totalUnread,
    receiveActivity,
    loadConversations,
    loadMoreConversations,
    startConversation,
    openConversation,
    loadOlderMessages,
    closeConversation,
    sendMessage,
    retryMessage,
    discardMessage,
    sendTyping,
    markLatestRead,
    removeMessage,
    toggleMessageLike,
  };
});
